import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles } from "lucide-react";
import { ChatMessage } from "./ChatMessage";
import { cn } from "@/lib/utils";
import { MOCK_COMPANIES, mapSearchTermToCategories } from "@/mock/companies";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatInterfaceProps {
  onServiceRequest: (request: string) => void;
  onCompleteBooking?: (data: any) => void;
  className?: string;
  structured?: boolean;
  onNavigateCompany?: (companyId: string) => void;
}

export const ChatInterface = ({ onServiceRequest, onCompleteBooking, className, structured = false, onNavigateCompany }: ChatInterfaceProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Ciao! Ti aiuto a trovare il servizio perfetto per la casa. Di cosa hai bisogno oggi? (pulizie, giardinaggio, ristrutturazioni, riparazioni, ecc.)",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const [step, setStep] = useState<{ service?: string; area?: number; urgency?: string; slot?: string; city?: string; date?: string; firstName?: string; lastName?: string; email?: string; phone?: string; serviceAddress?: string; billingAddress?: string; sameAddress?: boolean; notes?: string }>({});

  const [showProviderCards, setShowProviderCards] = useState(false);
  const [providers, setProviders] = useState<any[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);

  // -------- Free-form parser (robust to user writing all info together) --------
  const parseFieldsFromText = (text: string) => {
    const t = (text || "").trim();
    const out: Partial<typeof step> & { preferredProvider?: string } = {};
    // area: "80 mq", "80m2", "80 metri"
    const mArea = t.match(/(\d{2,4})\s*(mq|m2|m²|metri|metri\s*quadri)/i);
    if (mArea) {
      const n = parseInt(mArea[1], 10);
      if (!isNaN(n)) out.area = n;
    }
    // urgency: normale/urgente/oggi
    if (/\burgent[ea]\b/i.test(t) || /\bo?gg?i\b/i.test(t)) {
      out.urgency = /\bo?gg?i\b/i.test(t) ? "oggi" : "urgente";
    } else if (/\bnormal[ea]?\b/i.test(t)) {
      out.urgency = "normale";
    }
    // slot: 08-12, 12-16, 16-20
    const mSlot = t.match(/\b(0?8[-:]\s?1?2|12[-:]\s?16|16[-:]\s?20)\b/);
    if (mSlot) {
      const raw = mSlot[1].replace(/\s/g, "");
      const map: Record<string,string> = {
        "08-12": "08:00 - 12:00",
        "08:12": "08:00 - 12:00",
        "12-16": "12:00 - 16:00",
        "12:16": "12:00 - 16:00",
        "16-20": "16:00 - 20:00",
        "16:20": "16:00 - 20:00",
      };
      out.slot = map[raw.replace(":", "-")] || "08:00 - 12:00";
    }
    // date: AAAA-MM-GG o DD/MM/AAAA
    const mIso = t.match(/\b(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/);
    const mIt = t.match(/\b(0?[1-9]|[12]\d|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-](20\d{2})\b/);
    if (mIso) {
      out.date = mIso[0];
    } else if (mIt) {
      const dd = mIt[1].padStart(2, "0");
      const mm = mIt[2].padStart(2, "0");
      const yyyy = mIt[3];
      out.date = `${yyyy}-${mm}-${dd}`;
    }
    // city: "a Jesolo", "in Jesolo"
    const mCity = t.match(/\b(?:a|in)\s+([A-ZÀ-ÖØ-Þ][\wÀ-ÖØ-öø-ÿ'’\- ]{2,})\b/);
    if (mCity) {
      out.city = mCity[1].trim();
    }
    // preferred provider: "usa <nome>", "preferisco <nome>"
    const mProv = t.match(/\b(?:usa|preferisco)\s+([A-Za-z0-9À-ÖØ-öø-ÿ'’\-\s]{2,})$/i);
    if (mProv) {
      out.preferredProvider = mProv[1].trim();
    }
    // service: usa testo residuo per mappare categoria, ma salvalo come descrizione servizio
    // Se l'utente scrive "pulizie casa a Jesolo 80mq urgente", consideriamo "pulizie casa" il servizio
    // Rimuoviamo parti già riconosciute
    let svc = t
      .replace(mArea?.[0] || "", "")
      .replace(mSlot?.[0] || "", "")
      .replace(mIso?.[0] || "", "")
      .replace(mIt?.[0] || "", "")
      .replace(mCity?.[0] || "", "")
      .replace(mProv?.[0] || "", "")
      .trim();
    // Evita che parole come "oggi/urgente" rimangano nel servizio
    svc = svc.replace(/\burgent[ea]\b|\bo?gg?i\b|\bnormal[ea]?\b/gi, "").trim();
    if (svc && svc.length >= 3) {
      out.service = svc;
    }
    return out;
  };

  // Fetch providers from DB once we know service and city; fallback to MOCK if empty
  useEffect(() => {
    const fetchProviders = async () => {
      if (!step.city) return;
      setLoadingProviders(true);
      try {
        const city = String(step.city || "").trim();
        // Basic city match; only active and not deleted
        const { data, error } = await supabase
          .from("companies")
          .select("id, company_name, logo_url, rating, total_reviews, primary_categories, city, is_active, is_deleted")
          .eq("is_active", true)
          .eq("is_deleted", false)
          .ilike("city", `%${city}%`)
          .order("rating", { ascending: false })
          .order("total_reviews", { ascending: false })
          .limit(20);
        if (error) throw error;
        const mapped = (data || []).map((r: any) => ({
          id: r.id,
          name: r.company_name || "Azienda",
          image: r.logo_url || "https://placehold.co/96x96/png",
          rating: typeof r.rating === "number" ? r.rating : 0,
          reviewCount: typeof r.total_reviews === "number" ? r.total_reviews : 0,
          categories: Array.isArray(r.primary_categories) ? r.primary_categories : [],
          city: r.city || "",
          availability: "Questa settimana",
        }));
        if (mapped.length > 0) {
          setProviders(mapped);
        } else {
          // fallback to mock but keep filtering by city if present there
          const mockMapped = MOCK_COMPANIES
            .filter((c) => (c.cities || []).some((ct) => ct.toLowerCase().includes(city.toLowerCase())))
            .map((c) => ({
              id: c.id,
              name: c.name,
              image: c.image,
              rating: c.rating,
              reviewCount: c.reviewCount,
              categories: c.categories,
              city: (c.cities && c.cities[0]) || "",
              availability: c.availability,
            }));
          setProviders(mockMapped);
        }
      } catch {
        // On error: show mock as soft fallback
        const mock = MOCK_COMPANIES.map((c) => ({
          id: c.id,
          name: c.name,
          image: c.image,
          rating: c.rating,
          reviewCount: c.reviewCount,
          categories: c.categories,
          city: (c.cities && c.cities[0]) || "",
          availability: c.availability,
        }));
        setProviders(mock);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.city]);

  const getFilteredProviders = () => {
    const service = step.service?.toLowerCase() || "";
    const city = step.city?.toLowerCase() || "";
    let filtered = providers;
    if (service) {
      const cats = mapSearchTermToCategories(service);
      const catsLower = cats.map((c) => c.toLowerCase());
      if (catsLower.length > 0) {
        filtered = filtered.filter((p: any) => {
          const pcats = (p.categories || []).map((x: string) => String(x).toLowerCase());
          // Se il provider non ha categorie, non escluderlo (applicheremo il filtro città)
          if (pcats.length === 0) return true;
          return pcats.some((c: string) => catsLower.includes(c));
        });
      }
    }
    if (city) {
      filtered = filtered.filter((p: any) => {
        const pcity = String(p.city || "");
        return pcity.toLowerCase().includes(city);
      });
    }
    return filtered;
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    // Comandi rapidi
    const lower = input.trim().toLowerCase();
    if (lower === "ricomincia" || lower === "restart" || lower === "reset") {
      setTimeout(() => {
        setStep({});
        setShowProviderCards(false);
        setProviders([]);
        setVisibleCount(3);
        setMessages([
          {
            id: "1",
            text: "Ciao! Ti aiuto a trovare il servizio perfetto per la casa. Di cosa hai bisogno oggi? (pulizie, giardinaggio, ristrutturazioni, riparazioni, ecc.)",
            isUser: false,
            timestamp: new Date()
          }
        ]);
        setIsTyping(false);
      }, 300);
      return;
    }

    if (!structured) {
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
        text: "Perfetto! Ho trovato diverse aziende adatte alla tua richiesta. Ti mostro le migliori in base a disponibilità e recensioni.",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
        onServiceRequest(input);
      }, 1200);
      return;
    }

    // Structured onboarding flow
    const curr = { ...step };
    // Integra i campi dedotti dal messaggio libero
    const parsed = parseFieldsFromText(input);
    if (!curr.service && parsed.service) curr.service = parsed.service;
    if (!curr.area && parsed.area) curr.area = parsed.area;
    if (!curr.urgency && parsed.urgency) curr.urgency = parsed.urgency;
    if (!curr.city && parsed.city) curr.city = parsed.city;
    if (!curr.slot && parsed.slot) curr.slot = parsed.slot;
    if (!curr.date && parsed.date) curr.date = parsed.date;
    if ((parsed as any).preferredProvider && !('preferredProvider' in (curr as any))) {
      (curr as any).preferredProvider = (parsed as any).preferredProvider;
    }
    setStep(curr);
    if (!curr.service) {
      curr.service = input;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Perfetto. Quanti metri quadrati circa (es. 80)?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.area) {
      const parsed = parseInt(input.replace(/[^0-9]/g, ""), 10);
      curr.area = isNaN(parsed) ? 80 : parsed;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Urgenza? (normale / urgente / oggi)",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.urgency) {
      const val = input.toLowerCase();
      curr.urgency = ["normale", "urgente", "oggi"].includes(val) ? val : "normale";
      setStep(curr);
      setTimeout(() => {
        const aiResponse1: Message = {
          id: (Date.now() + 1).toString(),
        text: "In quale città/zona ti serve il servizio?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse1]);
        setIsTyping(false);
      }, 600);
      return;
    }

  // Raccogli prima la città, poi la fascia oraria
  if (!curr.city) {
    curr.city = input;
    setStep(curr);
    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: "Preferisci una fascia oraria? (08-12 / 12-16 / 16-20)",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 600);
    return;
  }

  if (!curr.slot) {
    const normalized = input.replace(/\s/g,"");
    const slots = ["08:00-12:00","12:00-16:00","16:00-20:00","08-12","12-16","16-20"]; 
    const map: Record<string,string> = {"08-12":"08:00 - 12:00","12-16":"12:00 - 16:00","16-20":"16:00 - 20:00","08:00-12:00":"08:00 - 12:00","12:00-16:00":"12:00 - 16:00","16:00-20:00":"16:00 - 20:00"};
    const found = slots.find(s => s.toLowerCase() === normalized.toLowerCase());
    curr.slot = found ? map[found] : "08:00 - 12:00";
    setStep(curr);
    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: "Hai un fornitore preferito? (scrivi il nome oppure 'no')",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 600);
    return;
  }

    // Preferred provider handling
    if (!('preferredProvider' in curr as any)) {
      const normalized = input.trim();
      if (normalized.toLowerCase() === 'no' || normalized.length === 0) {
        // Show provider cards to let the user pick
        setShowProviderCards(true);
        setTimeout(() => {
          const aiResponse: Message = {
            id: (Date.now() + 1).toString(),
            text: "Ecco alcune aziende consigliate. Scegline una per proseguire.",
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiResponse]);
          setIsTyping(false);
        }, 400);
        return;
      } else {
        (curr as any).preferredProvider = normalized;
        setStep(curr);
        setTimeout(() => {
          const aiResponse: Message = {
            id: (Date.now() + 1).toString(),
            text: `Perfetto, useremo ${normalized}. Preferisci una fascia oraria? (08-12 / 12-16 / 16-20)`,
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiResponse]);
          setIsTyping(false);
        }, 600);
        return;
      }
    }

    if (!curr.slot) {
      const normalized = input.replace(/\s/g,"");
      const slots = ["08:00-12:00","12:00-16:00","16:00-20:00","08-12","12-16","16-20"]; 
      const map: Record<string,string> = {"08-12":"08:00 - 12:00","12-16":"12:00 - 16:00","16-20":"16:00 - 20:00","08:00-12:00":"08:00 - 12:00","12:00-16:00":"12:00 - 16:00","16:00-20:00":"16:00 - 20:00"};
      const found = slots.find(s => s.toLowerCase() === normalized.toLowerCase());
      curr.slot = found ? map[found] : "08:00 - 12:00";
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "In quale città/zona ti serve il servizio?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.city) {
      curr.city = input;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Che data preferisci? (formato AAAA-MM-GG)",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.date) {
      const d = input.match(/\d{4}-\d{2}-\d{2}/)?.[0] || new Date().toISOString().slice(0,10);
      curr.date = d;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Il tuo nome?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.firstName) {
      curr.firstName = input.split(" ")[0];
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Il tuo cognome?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.lastName) {
      curr.lastName = input;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Email?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.email) {
      curr.email = input;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Telefono?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.phone) {
      curr.phone = input;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Indirizzo del servizio?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (!curr.serviceAddress) {
      curr.serviceAddress = input;
      setStep(curr);
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Usare lo stesso indirizzo per fatturazione? (sì/no)",
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 600);
      return;
    }

    if (curr.sameAddress === undefined) {
      const yes = input.trim().toLowerCase().startsWith('s');
      curr.sameAddress = yes;
      if (yes) {
        curr.billingAddress = curr.serviceAddress;
      }
      setStep(curr);
      if (!yes) {
        setTimeout(() => {
          const aiResponse: Message = {
            id: (Date.now() + 1).toString(),
            text: "Indirizzo di fatturazione?",
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiResponse]);
          setIsTyping(false);
        }, 600);
        return;
      }
    }

    if (!curr.billingAddress) {
      curr.billingAddress = input;
      setStep(curr);
    }

    if (curr.urgency && curr.area && curr.service && curr.slot && curr.city && curr.date && curr.firstName && curr.lastName && curr.email && curr.phone && curr.serviceAddress && curr.billingAddress && curr.notes === undefined) {
      curr.notes = '';
    }

    if (curr.urgency && curr.area && curr.service && curr.slot && curr.city && curr.date && curr.firstName && curr.lastName && curr.email && curr.phone && curr.serviceAddress && curr.billingAddress && curr.notes !== undefined) {
      const summary = `${curr.service} • ${curr.city} • ~${curr.area}mq • ${curr.urgency} • ${curr.slot}`;
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: `Ok, procedo alla conferma per: ${summary}`,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
        onCompleteBooking?.({
          date: new Date(curr.date!),
          time: curr.slot,
          firstName: curr.firstName,
          lastName: curr.lastName,
          email: curr.email,
          phone: curr.phone,
          serviceAddress: curr.serviceAddress,
          billingAddress: curr.billingAddress,
          useSameAddress: curr.sameAddress,
          notes: curr.notes,
          isRecurring: false,
          serviceType: "standard",
          urgency: curr.urgency === 'oggi' ? 'same_day' : (curr.urgency === 'urgente' ? 'urgent' : 'normal'),
          squareMeters: curr.area,
        });
      }, 800);
      return;
    }
      curr.notes = input.toLowerCase() === 'no' ? '' : input;
      setStep(curr);
      setTimeout(() => {
        const summary = `${curr.service} • ~${curr.area}mq • ${curr.urgency} • ${curr.slot}${curr.notes ? ' • note: ' + curr.notes : ''}`;
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: `Ok, cerco subito i migliori fornitori per: ${summary}`,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
        onServiceRequest(summary);
      }, 800);
      return;
  };

  const quickSuggestions = [
    "Pulizie domestiche",
    "Manutenzione giardino", 
    "Ristrutturazione bagno",
    "Piccole riparazioni"
  ];

  return (
    <div className={cn("flex flex-col h-full bg-gradient-card rounded-xl shadow-medium", className)}>
      {/* Chat Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-gradient-primary rounded-full">
            <Sparkles className="h-4 w-4 text-primary-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Assistente Servizi</h3>
            <p className="text-xs text-muted-foreground">Trova il servizio perfetto per la casa</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto space-y-2">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message.text}
            isUser={message.isUser}
            timestamp={message.timestamp}
          />
        ))}
        
        {/* Provider Cards */}
        {showProviderCards && (
          <div className="grid grid-cols-1 gap-3 mt-2">
            {getFilteredProviders().length === 0 && (
              <div className="text-xs text-muted-foreground p-3 border rounded-lg bg-card">
                Nessun fornitore trovato per la zona indicata. Prova a cambiare città o servizio.
              </div>
            )}
            {getFilteredProviders().slice(0, visibleCount).map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <img src={p.image} alt={p.name} className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">⭐ {p.rating} · {p.reviewCount} recensioni · {p.availability}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="secondary" 
                  onClick={() => {
                    // Vai direttamente alla pagina azienda
                    if (onNavigateCompany) {
                      onNavigateCompany(p.id);
                    } else {
                      window.location.href = `/company/${encodeURIComponent(p.id)}`;
                    }
                  }}
                >
                  Scegli
                </Button>
              </div>
            ))}
            {getFilteredProviders().length > visibleCount && (
              <Button 
                variant="outline" 
                size="sm" 
                className="justify-self-center mt-1"
                onClick={() => setVisibleCount((c) => c + 3)}
              >
                Mostra altri
              </Button>
            )}
          </div>
        )}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-chat-assistant text-chat-assistant-foreground px-4 py-3 rounded-2xl rounded-bl-md shadow-soft">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Suggestions */}
      {messages.length === 1 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-muted-foreground mb-2">Suggerimenti rapidi:</p>
          <div className="flex flex-wrap gap-2">
            {quickSuggestions.map((suggestion, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => setInput(suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Descrivi il servizio che ti serve..."
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            className="flex-1"
          />
          <Button 
            onClick={handleSend} 
            disabled={!input.trim() || isTyping}
            size="icon"
            variant="chat"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};